import { useEffect } from 'react';
import {
  // File menu
  FilePlus,
  FolderOpen,
  Cloud,
  Save,
  LayoutTemplate,
  Image as ImageIcon,
  X,
  // Edit
  Scissors,
  Copy,
  ClipboardPaste,
  // History + view
  Undo2,
  Redo2,
  Maximize,
  Grid3x3,
  Command,
  HelpCircle,
  // Tool palette
  MousePointer2,
  Plus,
  Crop,
  Pencil,
  Type,
  Square,
  Eraser,
  PaintBucket,
  Stamp,
  PenTool,
  Sparkles,
  // Transform
  RotateCcw,
  RotateCw,
  FlipHorizontal2,
  FlipVertical2,
  // Layers
  Eye,
  Trash2,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import type { CommandRegistry } from '../lib/command-registry';
import { useFormattedShortcut } from '../hooks/useCommandRegistry';

interface IconLegendDialogProps {
  onClose: () => void;
  /** Registry — drives the shortcut hint appended to names for
   *  entries with a `commandId`. Live-updates when the user remaps
   *  a binding in Settings → Keyboard. Tier 3 Group C Phase 2. */
  registry: CommandRegistry;
  isMac: boolean;
  /** Open the in-app documentation drawer. Closing the legend
   *  before opening the drawer keeps the focus model clean — two
   *  modals at once would steal keyboard focus from each other. */
  onOpenDocs: () => void;
}

interface IconEntry {
  icon: React.ComponentType<{ className?: string }>;
  /** Display name WITHOUT a shortcut hint. When `commandId` is set
   *  the renderer appends "(SHORTCUT)" reactively from the
   *  registry; otherwise the name is shown verbatim. */
  name: string;
  description: string;
  /** Registry command id whose effective shortcut drives the
   *  parenthetical hint. Omit for entries without a keyboard
   *  binding (e.g. toolbar-only icons). */
  commandId?: string;
}

interface IconGroup {
  title: string;
  entries: IconEntry[];
}

/** Exported for tests so a future lucide-react upgrade that renames an icon
 *  can be caught by an import-time assertion before it would render
 *  silently as `undefined`. */
export const ICON_LEGEND_GROUPS: IconGroup[] = [
  {
    title: 'File menu (folder icon, top-left)',
    entries: [
      { icon: FilePlus, name: 'New', description: 'Start a fresh blank canvas in a new tab.' },
      {
        icon: LayoutTemplate,
        name: 'New from Template…',
        description:
          'Start a new document from a preset (sized for common social, print, and screen targets).',
      },
      {
        icon: FolderOpen,
        name: 'Open Local',
        description:
          'Open an image from your device. Supports overwrite-in-place save in Chrome / Edge.',
      },
      {
        icon: Cloud,
        name: 'Open Fias',
        description: 'Open an image previously saved to Fias storage.',
      },
      {
        icon: Save,
        name: 'Save',
        description:
          'Save back to wherever the file was opened from (Fias re-upload, or local file overwrite when supported).',
      },
      {
        icon: Save,
        name: 'Save As…',
        description: 'Open the export dialog to choose name, format, dimensions, and destination.',
      },
      {
        icon: LayoutTemplate,
        name: 'Save as Template…',
        description:
          'Save the current canvas as a reusable template for future New-from-Template flows.',
      },
      {
        icon: ImageIcon,
        name: 'Document Settings…',
        description:
          'Edit document metadata: DPI, color mode, physical-unit readouts (read-only canvas size).',
      },
      {
        icon: ImageIcon,
        name: 'Canvas Size…',
        description:
          'Resize the canvas without resampling pixels. Pick an anchor (3×3 grid) and an extension fill color (White / Black / Transparent).',
      },
      {
        icon: X,
        name: 'Close',
        description: 'Close the active tab. Prompts to save when there are unsaved changes.',
      },
    ],
  },
  {
    title: 'Edit (top toolbar)',
    entries: [
      {
        icon: Scissors,
        name: 'Cut',
        commandId: 'edit.cut',
        description:
          'Cut the marqueed pixels from the active layer to the clipboard. Disabled until a marquee is finalized.',
      },
      {
        icon: Copy,
        name: 'Copy',
        commandId: 'edit.copy',
        description: 'Copy the marqueed pixels to the clipboard without removing them.',
      },
      {
        icon: ClipboardPaste,
        name: 'Paste',
        commandId: 'edit.paste',
        description: 'Paste the clipboard contents as a new layer at the original position.',
      },
    ],
  },
  {
    title: 'History (top toolbar)',
    entries: [
      {
        icon: Undo2,
        name: 'Undo',
        commandId: 'edit.undo',
        description: 'Step backward through your edit history.',
      },
      {
        icon: Redo2,
        name: 'Redo',
        commandId: 'edit.redo',
        description: 'Step forward through your edit history.',
      },
    ],
  },
  {
    title: 'View (top toolbar)',
    entries: [
      {
        icon: Maximize,
        name: 'Fit to screen',
        commandId: 'view.fit-screen',
        description:
          'Resize the canvas view to fit the editor area. Zoom in / out and the % readout live in the StatusBar (bottom-left).',
      },
      {
        icon: Grid3x3,
        name: 'Grid/Guide settings',
        description:
          'Toggle rulers, grid, guides, transparency checker, snap behaviors, and add / clear guides.',
      },
      {
        icon: Command,
        name: 'Command palette',
        commandId: 'palette.open',
        description: 'Search and run any editor command by name.',
      },
      { icon: HelpCircle, name: 'Help / icon legend', description: 'Open this dialog.' },
    ],
  },
  {
    title: 'Tools (left palette)',
    entries: [
      {
        icon: MousePointer2,
        name: 'Move',
        commandId: 'tool.move',
        description: 'Select and move objects on the canvas.',
      },
      {
        icon: Plus,
        name: 'Select / Marquee',
        commandId: 'tool.marquee',
        description: 'Make a selection (rectangle, ellipse, lasso, polygon, or wand).',
      },
      {
        icon: Crop,
        name: 'Crop',
        commandId: 'tool.crop',
        description: 'Crop the canvas to a chosen region.',
      },
      {
        icon: Pencil,
        name: 'Draw',
        commandId: 'tool.draw',
        description: 'Free-hand brush. Strokes bake into the active layer.',
      },
      {
        icon: Type,
        name: 'Text',
        commandId: 'tool.text',
        description: 'Add editable text. Click empty canvas to drop new text.',
      },
      {
        icon: Square,
        name: 'Shape',
        commandId: 'tool.shape',
        description: 'Add a rectangle, circle, triangle, or line.',
      },
      {
        icon: Eraser,
        name: 'Eraser',
        commandId: 'tool.eraser',
        description: 'Erase pixels on the active layer (true erase, not paint white).',
      },
      {
        icon: PaintBucket,
        name: 'Fill',
        commandId: 'tool.fill',
        description: 'Flood-fill color regions. Respects an active marquee.',
      },
      {
        icon: Stamp,
        name: 'Retouch',
        commandId: 'tool.retouch',
        description:
          'Variants for repairing pixels: Clone (sample-then-paint), Heal (texture-aware patching), Blur, Smudge, and Dodge / Burn.',
      },
      {
        icon: PenTool,
        name: 'Pen',
        description:
          'Place anchor points to draw editable vector paths. Path Edit mode lets you re-shape an existing vector after the fact.',
      },
      {
        icon: Sparkles,
        name: 'AI',
        commandId: 'tool.ai',
        description:
          'Picker dialog for AI capabilities: Smart Create / Smart Delete / Smart Erase / Inpaint / Outpaint / Search-and-Replace / Image-to-Image / Upscale / Vectorize / Remove Background / Replace Background.',
      },
    ],
  },
  {
    title: 'Transform (Move tool properties)',
    entries: [
      {
        icon: RotateCcw,
        name: 'Rotate left 90°',
        description: 'Rotate the entire canvas counter-clockwise.',
      },
      {
        icon: RotateCw,
        name: 'Rotate right 90°',
        description: 'Rotate the entire canvas clockwise.',
      },
      {
        icon: FlipHorizontal2,
        name: 'Flip horizontal',
        description: 'Mirror the canvas left ↔ right.',
      },
      {
        icon: FlipVertical2,
        name: 'Flip vertical',
        description: 'Mirror the canvas top ↔ bottom.',
      },
    ],
  },
  {
    title: 'Layers panel',
    entries: [
      {
        icon: Plus,
        name: 'New layer',
        description: 'Add a fresh empty layer above the current one.',
      },
      {
        icon: Eye,
        name: 'Toggle visibility',
        description: 'Show or hide a layer without deleting it.',
      },
      {
        icon: ChevronUp,
        name: 'Move layer up',
        description: 'Bring the layer one step toward the front.',
      },
      {
        icon: ChevronDown,
        name: 'Move layer down',
        description: 'Send the layer one step toward the back.',
      },
      {
        icon: Trash2,
        name: 'Delete layer',
        description:
          'Remove a layer and everything on it. Lives in the per-layer overflow (⋯) menu.',
      },
    ],
  },
];

/** Modal listing every toolbar / palette / panel icon in the editor with a
 *  short description, so users (especially on mobile, where hover tooltips
 *  don't apply) can discover what each control does. */
export function IconLegendDialog({ onClose, registry, isMac, onOpenDocs }: IconLegendDialogProps) {
  // Close on Escape — matches the rest of the editor's modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <h2 className="text-base font-semibold">Icon reference</h2>
          <button
            type="button"
            aria-label="Close"
            className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-3 space-y-5">
          {/* Documentation entry — single CTA at the top of the
              legend, since users opening "icon reference" are
              often hunting for explanations the legend doesn't
              cover. The drawer's TOC + per-panel ? buttons are
              the deeper paths. */}
          <button
            type="button"
            onClick={onOpenDocs}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md border border-border bg-background hover:bg-accent transition-colors text-left"
          >
            <HelpCircle className="h-5 w-5 text-foreground shrink-0" />
            <span className="flex flex-col gap-0.5 min-w-0">
              <span className="text-sm font-medium">Open documentation</span>
              <span className="text-xs text-muted-foreground">
                Searchable in-app help for every panel and tool
              </span>
            </span>
          </button>
          {ICON_LEGEND_GROUPS.map((group) => (
            <section key={group.title} className="space-y-2">
              <h3 className="text-xs font-medium uppercase text-muted-foreground tracking-wide">
                {group.title}
              </h3>
              <ul className="space-y-2">
                {group.entries.map((entry, i) => (
                  <IconLegendRow
                    key={`${group.title}-${entry.name}-${i}`}
                    entry={entry}
                    registry={registry}
                    isMac={isMac}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Row component split out so each row's `useFormattedShortcut`
 *  call mounts its own subscription — keeps the render cost small
 *  even though the dialog has ~30 rows. */
function IconLegendRow({
  entry,
  registry,
  isMac,
}: {
  entry: IconEntry;
  registry: CommandRegistry;
  isMac: boolean;
}) {
  const Icon = entry.icon;
  const shortcut = useFormattedShortcut(registry, entry.commandId ?? '', isMac);
  const name = entry.commandId && shortcut ? `${entry.name} (${shortcut})` : entry.name;
  return (
    <li className="flex items-start gap-3">
      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent text-foreground shrink-0">
        <Icon className="h-4 w-4" />
      </span>
      <div className="text-sm flex-1">
        <div className="font-medium">{name}</div>
        <div className="text-xs text-muted-foreground">{entry.description}</div>
      </div>
    </li>
  );
}
